import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for Google reCAPTCHA Enterprise integration
 * Dynamically loads the reCAPTCHA Enterprise script and provides token generation
 *
 * @returns {Object} Hook state and methods
 * @property {boolean} isReady - Whether reCAPTCHA is loaded and ready
 * @property {boolean} isExecuting - Whether a token generation is in progress
 * @property {string|null} error - Any error that occurred during loading or execution
 * @property {Function} executeRecaptcha - Function to generate a reCAPTCHA token with an action
 */
function useRecaptchaEnterprise() {
  const [isReady, setIsReady] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState(null);
  const scriptLoadedRef = useRef(false);

  const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    // Don't load script if already loaded
    if (scriptLoadedRef.current) {
      return;
    }

    // Check if site key is configured
    if (!siteKey) {
      setError("reCAPTCHA site key is not configured");
      return;
    }

    // Check if script already exists in the document
    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');

    if (existingScript) {
      scriptLoadedRef.current = true;
      // Wait for grecaptcha.enterprise to be available
      checkRecaptchaReady();
      return;
    }

    // Create and load the reCAPTCHA Enterprise script
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      scriptLoadedRef.current = true;
      checkRecaptchaReady();
    };

    script.onerror = () => {
      setError("Failed to load reCAPTCHA script");
      scriptLoadedRef.current = false;
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: Remove script if component unmounts
      // Note: In practice, you might want to keep the script loaded
      // if multiple components use reCAPTCHA
    };
  }, [siteKey]);

  /**
   * Checks if grecaptcha.enterprise is available and ready
   * Uses polling with timeout as a fallback
   */
  const checkRecaptchaReady = useCallback(() => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds with 100ms intervals

    const checkInterval = setInterval(() => {
      attempts++;

      if (window.grecaptcha && window.grecaptcha.enterprise && window.grecaptcha.enterprise.ready) {
        clearInterval(checkInterval);
        window.grecaptcha.enterprise.ready(() => {
          setIsReady(true);
          setError(null);
        });
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        setError("reCAPTCHA failed to initialize");
      }
    }, 100);
  }, []);

  /**
   * Executes reCAPTCHA and returns a token
   * @param {string} action - The action name for analytics (e.g., 'contact_form', 'newsletter_signup')
   * @returns {Promise<string|null>} The reCAPTCHA token or null if failed
   */
  const executeRecaptcha = useCallback(
    async (action) => {
      if (!isReady) {
        setError("reCAPTCHA is not ready yet");
        return null;
      }

      if (!action) {
        setError("Action parameter is required");
        return null;
      }

      setIsExecuting(true);
      setError(null);

      try {
        const token = await window.grecaptcha.enterprise.execute(siteKey, {
          action,
        });
        setIsExecuting(false);
        return token;
      } catch (err) {
        setIsExecuting(false);
        setError("Failed to execute reCAPTCHA");
        console.error("reCAPTCHA execution error:", err);
        return null;
      }
    },
    [isReady, siteKey]
  );

  return {
    isReady,
    isExecuting,
    error,
    executeRecaptcha,
  };
}

export default useRecaptchaEnterprise;
